import { useState, type FormEvent } from 'react'
import { EndpointHint } from '../../core/index.js'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Invitation, InvitationResult, WorkerInvitationState, WorkerLogin } from '../index.js'
import { SecretBanner } from '../../core/index.js'
import { FORM_INPUT_CLASS, FormError } from '../../core/index.js'
import type { WorkerLoginApi } from './workerLoginApi.js'
import { Button } from '../../core/index.js'
import { KeyRound, MailPlus, Save, Send, XCircle } from 'lucide-react'
import { LoadFailure } from '../../core/index.js'
import { InvitationHandOffBanner } from './InviteOperatorModal.js'
import { PhoneInput } from '../../core/index.js'

const LINK_BUTTON = 'text-[11px] font-bold text-marine hover:text-marine-dk disabled:opacity-40'
const DANGER_BUTTON = 'text-[11px] font-bold text-coral hover:text-coral/80 disabled:opacity-40'

interface WorkerLoginSectionProps {
  api: WorkerLoginApi
  queryKey: readonly string[]
  /** False while the caller has no client id yet; the section then only explains itself. */
  enabled?: boolean
}

function formatDateTime(iso: string): string {
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
}

/**
 * A worker's own sign-in to the worker app, managed by an operator, a
 * facility administrator at their own facility or the platform admin.
 * Since invitations phase 3 the usual way in is **Invite to the app**: the
 * login is made pending and the worker is emailed (and texted, where asked)
 * a link and a code to set their own password with; "Create with a
 * password" stays behind a link for a demo (plan D8). Once there is a
 * login: change the email, issue a new password, hand out a reset link,
 * or remove it. The worker's roster record is never touched from here.
 * Shared by the operator's Edit worker dialog and the admin's worker
 * detail through the adapter.
 */
export function WorkerLoginSection({ api, queryKey, enabled = true }: WorkerLoginSectionProps) {
  const queryClient = useQueryClient()
  const loginQuery = useQuery({ queryKey, queryFn: api.get, enabled })
  const invitationKey = [...queryKey, 'invitation']
  const invitationQuery = useQuery({ queryKey: invitationKey, queryFn: api.invitation, enabled: enabled && (loginQuery.data === null || loginQuery.data?.invitationStatus === 'pending') })
  const [secret, setSecret] = useState<{ label: string; value: string } | null>(null)
  const [invited, setInvited] = useState<InvitationResult | null>(null)
  const [editingEmail, setEditingEmail] = useState(false)
  const [confirmingRemove, setConfirmingRemove] = useState(false)
  const [withPassword, setWithPassword] = useState(false)

  const refresh = () => Promise.all([queryClient.invalidateQueries({ queryKey }), queryClient.invalidateQueries({ queryKey: invitationKey })])

  const invite = useMutation({
    mutationFn: api.invite,
    onSuccess: (result) => {
      setInvited(result)
      setWithPassword(false)
      void refresh()
    },
  })
  const resendInvitation = useMutation({
    mutationFn: api.resendInvitation,
    onSuccess: (result) => {
      setInvited(result)
      void refresh()
    },
  })
  const revokeInvitation = useMutation({
    mutationFn: api.revokeInvitation,
    onSuccess: () => {
      setInvited(null)
      setConfirmingRemove(false)
      void refresh()
    },
  })

  const create = useMutation({
    mutationFn: api.create,
    onSuccess: (created) => {
      setSecret({ label: 'One-time password — copy it now, it won’t be shown again', value: created.password })
      void refresh()
    },
  })
  const update = useMutation({
    mutationFn: api.update,
    onSuccess: (updated) => {
      if (updated.password) {
        setSecret({ label: 'New password — copy it now, it won’t be shown again', value: updated.password })
      }
      setEditingEmail(false)
      void refresh()
    },
  })
  const remove = useMutation({
    mutationFn: api.remove,
    onSuccess: () => {
      setConfirmingRemove(false)
      setSecret(null)
      void refresh()
    },
  })
  const resetLink = useMutation({
    mutationFn: api.resetLink,
    onSuccess: (link) => setSecret({ label: 'Reset link — send it to this worker directly. It expires in 1 hour.', value: link.resetUrl }),
  })

  const busy = create.isPending || update.isPending || remove.isPending || resetLink.isPending || invite.isPending || resendInvitation.isPending || revokeInvitation.isPending
  const error = create.error ?? update.error ?? remove.error ?? resetLink.error ?? invite.error ?? resendInvitation.error ?? revokeInvitation.error
  const login = loginQuery.data

  return (
    <div className="space-y-3">
      <div className="flex items-baseline gap-2 border-b border-marine/15 pb-1">
        <h3 className="text-[10px] font-bold uppercase tracking-wide text-marine">Worker login</h3>
        <EndpointHint>…/workers/{'{workerId}'}/login</EndpointHint>
      </div>

      {!enabled || loginQuery.isPending ? (
        <div className="text-xs text-ink-mid">{enabled ? 'Loading…' : 'Sign in again to manage this worker’s login.'}</div>
      ) : loginQuery.isError ? (
        <LoadFailure size="xs" message="The login could not be loaded." onRetry={() => void loginQuery.refetch()} retrying={loginQuery.isFetching} />
      ) : login === undefined ? null : login === null ? (
        withPassword ? (
          <CreateLoginForm busy={busy} onCreate={(email) => create.mutate({ email })} onInviteInstead={() => setWithPassword(false)} />
        ) : (
          <InviteForm busy={busy} state={invitationQuery.data ?? null} onInvite={(input) => invite.mutate(input)} onCreateInstead={() => setWithPassword(true)} />
        )
      ) : login.invitationStatus === 'pending' ? (
        <PendingLogin
          login={login}
          invitation={invitationQuery.data?.invitation ?? null}
          busy={busy}
          confirmingWithdraw={confirmingRemove}
          onResend={() => resendInvitation.mutate()}
          onConfirmWithdraw={() => setConfirmingRemove(true)}
          onCancelWithdraw={() => setConfirmingRemove(false)}
          onWithdraw={() => revokeInvitation.mutate()}
        />
      ) : (
        <ExistingLogin
          login={login}
          busy={busy}
          editingEmail={editingEmail}
          confirmingRemove={confirmingRemove}
          onEditEmail={() => setEditingEmail(true)}
          onCancelEmail={() => setEditingEmail(false)}
          onChangeEmail={(email) => update.mutate({ email })}
          onRotate={() => update.mutate({ rotatePassword: true })}
          onResetLink={() => resetLink.mutate()}
          onConfirmRemove={() => setConfirmingRemove(true)}
          onCancelRemove={() => setConfirmingRemove(false)}
          onRemove={() => remove.mutate()}
        />
      )}

      {secret && <SecretBanner label={secret.label} value={secret.value} onDismiss={() => setSecret(null)} />}
      {invited && <InvitationHandOffBanner result={invited} onDismiss={() => setInvited({ ...invited, handOff: null })} />}
      <FormError error={error} fallback="The login could not be changed." />
    </div>
  )
}

/** Invite to the app (invitations plan phase 3): the email, and a text as well to the known mobile or one typed here. */
function InviteForm({ busy, state, onInvite, onCreateInstead }: { busy: boolean; state: WorkerInvitationState | null; onInvite: (input: { email: string; text?: boolean; mobile?: string }) => void; onCreateInstead: () => void }) {
  const [email, setEmail] = useState('')
  const [textKnown, setTextKnown] = useState(false)
  const [mobile, setMobile] = useState('')
  const canSubmit = email.trim() !== '' && !busy

  function handleSubmit(event: FormEvent): void {
    event.preventDefault()
    if (!canSubmit) {
      return
    }
    const typed = mobile.trim()
    onInvite({ email: email.trim(), ...(typed !== '' ? { mobile: typed } : textKnown && state?.mobile ? { text: true } : {}) })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-2">
      <p className="text-xs text-ink-mid">
        No login yet. Invite this worker and they are emailed a link and a six-digit code, good for seven days, to set their own password with; nobody sees a password.
        {state && !state.canEmail && ' This deployment cannot email, so the link will be shown here for you to pass on.'}
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex-1 min-w-[14rem]">
          <span className="block text-[10px] font-bold uppercase tracking-wide text-ink-mid mb-1">Email (their username)</span>
          <input type="email" required autoComplete="off" value={email} onChange={(event) => setEmail(event.target.value)} className={FORM_INPUT_CLASS} disabled={busy} />
        </label>
        <Button icon={MailPlus} type="submit" disabled={!canSubmit}>
          {busy ? 'Inviting…' : 'Invite to the app'}
        </Button>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        {state?.mobile ? (
          <label className="flex items-center gap-2 text-[11px] text-ink">
            <input type="checkbox" checked={textKnown} onChange={(event) => setTextKnown(event.target.checked)} disabled={busy || mobile.trim() !== ''} />
            Also text them at {state.mobile}
          </label>
        ) : null}
        <label className="min-w-[12rem]">
          <span className="block text-[10px] font-bold uppercase tracking-wide text-ink-mid mb-1">{state?.mobile ? 'Or text a different mobile' : 'Also text a mobile (optional)'}</span>
          <PhoneInput autoComplete="off" value={mobile} onChange={setMobile} className={FORM_INPUT_CLASS} disabled={busy} />
        </label>
      </div>
      <button type="button" onClick={onCreateInstead} disabled={busy} className={LINK_BUTTON}>
        Create with a password instead (for a demo)
      </button>
    </form>
  )
}

/** The login made at the invite, waiting for the worker to set a password: resend, or withdraw it and the login together. */
function PendingLogin({ login, invitation, busy, confirmingWithdraw, onResend, onConfirmWithdraw, onCancelWithdraw, onWithdraw }: { login: WorkerLogin; invitation: Invitation | null; busy: boolean; confirmingWithdraw: boolean; onResend: () => void; onConfirmWithdraw: () => void; onCancelWithdraw: () => void; onWithdraw: () => void }) {
  const when = (iso: string) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  return (
    <div className="space-y-3">
      <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-3">
        <div className="min-w-0 sm:col-span-1">
          <dt className="text-[10px] font-bold uppercase tracking-wide text-ink-mid">Email</dt>
          <dd className="text-xs text-ink break-words font-mono select-all">{login.email}</dd>
        </div>
        <div className="sm:col-span-2">
          <dt className="text-[10px] font-bold uppercase tracking-wide text-ink-mid">Invitation</dt>
          <dd className="text-xs text-ink">
            <span className="rounded bg-gold/20 px-1.5 py-0.5 text-[10px] font-bold">Invited · waiting</span>{' '}
            {invitation ? (
              <>
                sent {when(invitation.createdAt)}
                {invitation.invitedBy.name ? ` by ${invitation.invitedBy.name}` : ''} · {invitation.status === 'expired' ? 'expired' : `expires ${when(invitation.expiresAt)}`}
                {invitation.sends.some((send) => send.channel === 'sms' && send.status === 'sent') ? ' · emailed and texted' : ''}
              </>
            ) : (
              'the worker sets their own password from it'
            )}
          </dd>
        </div>
      </dl>
      {confirmingWithdraw ? (
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-[11px] text-ink">Withdraw the invitation? The pending login goes with it; the worker stays on the roster.</span>
          <button type="button" onClick={onWithdraw} disabled={busy} className={DANGER_BUTTON}>
            {busy ? 'Withdrawing…' : 'Yes, withdraw'}
          </button>
          <button type="button" onClick={onCancelWithdraw} disabled={busy} className={LINK_BUTTON}>
            Keep it
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <Button icon={Send} variant="secondary" size="sm" onClick={onResend} disabled={busy}>
            Resend
          </Button>
          <Button icon={XCircle} variant="secondary" size="sm" onClick={onConfirmWithdraw} disabled={busy}>
            Withdraw
          </Button>
        </div>
      )}
    </div>
  )
}

function CreateLoginForm({ busy, onCreate, onInviteInstead }: { busy: boolean; onCreate: (email: string) => void; onInviteInstead: () => void }) {
  const [email, setEmail] = useState('')

  function handleSubmit(event: FormEvent): void {
    event.preventDefault()
    if (email.trim() !== '') {
      onCreate(email.trim())
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-2">
      <p className="text-xs text-ink-mid">A login with a generated password, shown once, for a demo: with it this worker can sign in to the worker app and see their own score, messages and progress.</p>
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex-1 min-w-[14rem]">
          <span className="block text-[10px] font-bold uppercase tracking-wide text-ink-mid mb-1">Email (their username)</span>
          <input type="email" required autoComplete="off" value={email} onChange={(event) => setEmail(event.target.value)} className={FORM_INPUT_CLASS} disabled={busy} />
        </label>
        <Button icon={KeyRound} type="submit" disabled={busy || email.trim() === ''}>
          {busy ? 'Creating…' : 'Create with a password'}
        </Button>
      </div>
      <button type="button" onClick={onInviteInstead} disabled={busy} className={LINK_BUTTON}>
        Invite them instead
      </button>
    </form>
  )
}

interface ExistingLoginProps {
  login: WorkerLogin
  busy: boolean
  editingEmail: boolean
  confirmingRemove: boolean
  onEditEmail: () => void
  onCancelEmail: () => void
  onChangeEmail: (email: string) => void
  onRotate: () => void
  onResetLink: () => void
  onConfirmRemove: () => void
  onCancelRemove: () => void
  onRemove: () => void
}

function ExistingLogin(props: ExistingLoginProps) {
  const { login, busy, editingEmail, confirmingRemove } = props
  return (
    <div className="space-y-3">
      <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-3">
        <div className="min-w-0 sm:col-span-1">
          <dt className="text-[10px] font-bold uppercase tracking-wide text-ink-mid">Email</dt>
          <dd className="text-xs text-ink break-words font-mono select-all">{login.email}</dd>
        </div>
        <div>
          <dt className="text-[10px] font-bold uppercase tracking-wide text-ink-mid">{login.invitationStatus === 'accepted' ? 'Invitation' : 'Created'}</dt>
          <dd className="text-xs text-ink">
            {login.invitationStatus === 'accepted' ? (login.invitationAcceptedAt ? `Accepted ${formatDateTime(login.invitationAcceptedAt)}` : 'Accepted') : `${formatDateTime(login.createdAt)} with a password`}
          </dd>
        </div>
        <div>
          <dt className="text-[10px] font-bold uppercase tracking-wide text-ink-mid">Last signed in</dt>
          <dd className="text-xs text-ink">{login.lastSignedInAt ? formatDateTime(login.lastSignedInAt) : 'Never'}</dd>
        </div>
      </dl>

      {editingEmail ? (
        <ChangeEmailForm current={login.email} busy={busy} onSave={props.onChangeEmail} onCancel={props.onCancelEmail} />
      ) : confirmingRemove ? (
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-[11px] text-ink">Remove this login? The worker stays on the roster; they just can’t sign in.</span>
          <button type="button" onClick={props.onRemove} disabled={busy} className={DANGER_BUTTON}>
            {busy ? 'Removing…' : 'Yes, remove'}
          </button>
          <button type="button" onClick={props.onCancelRemove} disabled={busy} className={LINK_BUTTON}>
            Keep it
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-4">
          <button type="button" onClick={props.onEditEmail} disabled={busy} className={LINK_BUTTON}>
            Change email
          </button>
          <button type="button" onClick={props.onRotate} disabled={busy} className={LINK_BUTTON}>
            New password
          </button>
          <button type="button" onClick={props.onResetLink} disabled={busy} className={LINK_BUTTON}>
            Reset link
          </button>
          <button type="button" onClick={props.onConfirmRemove} disabled={busy} className={`${DANGER_BUTTON} ml-auto`}>
            Remove login
          </button>
        </div>
      )}
    </div>
  )
}

function ChangeEmailForm({ current, busy, onSave, onCancel }: { current: string; busy: boolean; onSave: (email: string) => void; onCancel: () => void }) {
  const [email, setEmail] = useState(current)
  const changed = email.trim() !== '' && email.trim().toLowerCase() !== current

  function handleSubmit(event: FormEvent): void {
    event.preventDefault()
    if (changed) {
      onSave(email.trim())
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-2">
      <label className="flex-1 min-w-[14rem]">
        <span className="block text-[10px] font-bold uppercase tracking-wide text-ink-mid mb-1">New email</span>
        <input type="email" required autoFocus value={email} onChange={(event) => setEmail(event.target.value)} className={FORM_INPUT_CLASS} disabled={busy} />
      </label>
      <Button icon={Save} type="submit" disabled={busy || !changed}>
        {busy ? 'Saving…' : 'Save email'}
      </Button>
      <button type="button" onClick={onCancel} disabled={busy} className={LINK_BUTTON}>
        Cancel
      </button>
    </form>
  )
}
